import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, ChevronLeft, LogOut, Pencil, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fieldCls = "w-full bg-argali-obsidian border border-argali-brown/40 text-argali-ivory text-sm px-3 py-2 outline-none focus:border-argali-bronze";
const labelCls = "text-[0.6rem] uppercase tracking-[0.16em] text-argali-sandstone/60 mb-1 block";
const secondaryBtn = "border border-argali-brown/50 text-argali-sandstone hover:text-argali-gold hover:border-argali-bronze px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors";

const EVENT_LABELS = {
  available: "Свободен",
  personal_booking: "Личное бронирование",
  holiday: "Отпуск / выходной",
  busy: "Занят / недоступен",
  argali_booking: "АРГАЛИ — подтверждено",
  argali_tentative: "АРГАЛИ — предварительно",
};

const EVENT_MARKS = {
  available: "СВОБОДЕН",
  personal_booking: "ЛИЧНОЕ",
  holiday: "ВЫХОДНОЙ",
  busy: "ЗАНЯТ",
  argali_booking: "АРГАЛИ",
  argali_tentative: "АРГАЛИ?",
};

const managerRole = (user) => user?.role === "owner" || user?.role === "admin";

const localToday = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const addDays = (date, days) => {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const formatDate = (date) => {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("ru-RU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    .format(new Date(y, m - 1, d));
};

const formatShortDate = (date) => {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", year: "numeric" })
    .format(new Date(y, m - 1, d));
};

const timePart = (value) => String(value || "").slice(11, 16);
const datePart = (value) => String(value || "").slice(0, 10);

const emptyForm = () => ({
  id: null,
  event_type: "available",
  start_date: localToday(),
  end_date: localToday(),
  all_day: false,
  start_time: "09:00",
  end_time: "18:00",
  title: "",
  notes: "",
});

export const AdminScheduleShortcut = () => {
  const { user } = useAuth();
  if (!user || !user.role) return null;
  return (
    <Link
      to="/admin/schedule"
      className="fixed z-[70] right-5 md:right-8 top-[82px] border border-argali-bronze/60 bg-argali-obsidian/95 text-argali-gold hover:text-argali-ivory hover:border-argali-gold px-4 py-2.5 text-[0.66rem] uppercase tracking-[0.15em] flex items-center gap-2 shadow-xl"
      data-testid="schedule-shortcut"
    >
      <CalendarDays size={15} /> Расписание
    </Link>
  );
};

export default function AdminSchedule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const manager = managerRole(user);
  const [guides, setGuides] = useState([]);
  const [guideId, setGuideId] = useState("");
  const [from, setFrom] = useState(localToday());
  const [to, setTo] = useState(addDays(localToday(), 30));
  const [items, setItems] = useState([]);
  const [timezone, setTimezone] = useState("Asia/Barnaul");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [filter, setFilter] = useState("all");

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const loadGuides = async () => {
    if (!manager) return;
    try {
      const { data } = await api.get("/admin/guides");
      setGuides(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const load = async () => {
    if (!user || !user.role) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      if (manager && guideId) params.set("guide_id", guideId);
      const { data } = await api.get(`/schedule?${params.toString()}`);
      setItems(data.items || []);
      setTimezone(data.timezone || "Asia/Barnaul");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.role) loadGuides(); }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (user?.role) load(); }, [user?.role, guideId, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (manager) return;
    const start = `${form.start_date}T${form.all_day ? "00:00" : form.start_time}`;
    const end = `${form.end_date}T${form.all_day ? "23:59" : form.end_time}`;
    setSaving(true);
    try {
      const payload = {
        event_type: form.event_type,
        title: form.title,
        notes: form.notes,
        start_at: start,
        end_at: end,
        all_day: form.all_day,
      };
      if (form.id) {
        await api.patch(`/schedule/${form.id}`, payload);
        toast.success("Запись расписания обновлена");
      } else {
        await api.post("/schedule", payload);
        toast.success("Добавлено в расписание");
      }
      setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (event) => {
    if (manager || event.read_only) return;
    setForm({
      id: event.id,
      event_type: event.event_type,
      start_date: datePart(event.start_at),
      end_date: datePart(event.end_at),
      all_day: !!event.all_day,
      start_time: timePart(event.start_at) || "09:00",
      end_time: timePart(event.end_at) || "18:00",
      title: event.title || "",
      notes: event.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (event) => {
    if (manager || event.read_only) return;
    if (!window.confirm("Удалить эту запись из расписания?")) return;
    try {
      await api.delete(`/schedule/${event.id}`);
      toast.success("Запись удалена");
      if (form.id === event.id) setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const visibleItems = useMemo(() => filter === "all" ? items : items.filter((item) => item.event_type === filter), [items, filter]);
  const grouped = useMemo(() => {
    const result = {};
    visibleItems.forEach((item) => {
      const date = datePart(item.start_at);
      if (!result[date]) result[date] = [];
      result[date].push(item);
    });
    return result;
  }, [visibleItems]);

  const counts = useMemo(() => items.reduce((acc, item) => {
    acc[item.event_type] = (acc[item.event_type] || 0) + 1;
    return acc;
  }, {}), [items]);

  if (user === null) return <div className="min-h-screen" style={{ background: "#11110F" }} />;
  if (!user || !user.role) return null;

  return (
    <div className="min-h-screen" style={{ background: "#11110F" }} data-testid="admin-schedule-page">
      <header className="flex items-center justify-between px-5 md:px-10 h-[72px] border-b border-argali-brown/30">
        <div className="flex items-center gap-2.5 min-w-0">
          <img src="/brand/argali_emblem_ru.png" alt="Логотип АРГАЛИ" width="543" height="501" className="h-8 w-auto" />
          <span className="font-serif text-xl md:text-2xl tracking-[0.2em] text-argali-ivory">АРГАЛИ</span>
          <span className="eyebrow ml-2 hidden sm:inline">Расписание</span>
        </div>
        <div className="flex items-center gap-3 md:gap-5">
          <Link to="/admin" className="flex items-center gap-1.5 text-argali-sandstone/80 hover:text-argali-gold text-sm"><ChevronLeft size={16} /> Кабинет</Link>
          <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-argali-sandstone/80 hover:text-argali-gold text-sm"><LogOut size={16} /> Выйти</button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-9 md:py-12">
        <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-6 mb-8">
          <div>
            <p className="eyebrow mb-2">{manager ? "Расписание команды" : "Моё расписание"}</p>
            <h1 className="font-serif text-3xl md:text-4xl text-argali-ivory">Свободное время и занятость</h1>
            <p className="text-sm text-argali-sandstone/55 mt-2 max-w-2xl">
              Часовой пояс: Алтай · {timezone}. Личные бронирования, занятость и выходные видны только внутри кабинета АРГАЛИ и не публикуются на сайте.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
            <div className="border border-argali-brown/30 px-4 py-3"><span className="block text-xl text-argali-ivory">{counts.available || 0}</span><span className="text-[0.58rem] uppercase tracking-wider text-argali-sandstone/50">Свободно</span></div>
            <div className="border border-argali-brown/30 px-4 py-3"><span className="block text-xl text-argali-ivory">{(counts.personal_booking || 0) + (counts.busy || 0) + (counts.holiday || 0)}</span><span className="text-[0.58rem] uppercase tracking-wider text-argali-sandstone/50">Личная занятость</span></div>
            <div className="border border-argali-brown/30 px-4 py-3 col-span-2 sm:col-span-1"><span className="block text-xl text-argali-ivory">{(counts.argali_booking || 0) + (counts.argali_tentative || 0)}</span><span className="text-[0.58rem] uppercase tracking-wider text-argali-sandstone/50">АРГАЛИ</span></div>
          </div>
        </div>

        <section className="border border-argali-brown/30 p-5 md:p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {manager && (
              <div>
                <label className={labelCls}>Гид</label>
                <select className={fieldCls} value={guideId} onChange={(e) => setGuideId(e.target.value)}>
                  <option value="">Все гиды</option>
                  {guides.map((guide) => <option key={guide.id} value={guide.id}>{guide.name} · {guide.status}</option>)}
                </select>
              </div>
            )}
            <div><label className={labelCls}>С даты</label><input type="date" className={fieldCls} value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div><label className={labelCls}>По дату</label><input type="date" className={fieldCls} value={to} onChange={(e) => setTo(e.target.value)} /></div>
          </div>
        </section>

        {!manager && (
          <section className="border border-argali-brown/30 p-5 md:p-7 mb-8">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <p className="eyebrow mb-1">{form.id ? "Редактирование" : "Новая запись"}</p>
                <h2 className="font-serif text-2xl text-argali-ivory">{form.id ? "Изменить расписание" : "Добавить в расписание"}</h2>
              </div>
              {form.id && <button className={secondaryBtn} onClick={() => setForm(emptyForm())}>Отмена</button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              <div><label className={labelCls}>Тип</label><select className={fieldCls} value={form.event_type} onChange={(e) => { const value = e.target.value; setForm((f) => ({ ...f, event_type: value, all_day: value === "holiday" ? true : f.all_day })); }}><option value="available">Свободен</option><option value="personal_booking">Личное бронирование</option><option value="holiday">Отпуск / выходной</option><option value="busy">Занят / недоступен</option></select></div>
              <div><label className={labelCls}>С даты</label><input type="date" className={fieldCls} value={form.start_date} onChange={(e) => { const value = e.target.value; setForm((f) => ({ ...f, start_date: value, end_date: f.end_date < value ? value : f.end_date })); }} /></div>
              <div><label className={labelCls}>По дату</label><input type="date" className={fieldCls} min={form.start_date} value={form.end_date} onChange={(e) => setF("end_date", e.target.value)} /></div>
              <label className="flex items-end gap-3 text-sm text-argali-ivory/75 pb-2"><input type="checkbox" className="accent-argali-bronze" checked={form.all_day} onChange={(e) => setF("all_day", e.target.checked)} /> Весь день</label>
              <div><label className={labelCls}>Начало</label><input type="time" disabled={form.all_day} className={fieldCls + (form.all_day ? " opacity-40" : "")} value={form.start_time} onChange={(e) => setF("start_time", e.target.value)} /></div>
              <div><label className={labelCls}>Окончание</label><input type="time" disabled={form.all_day} className={fieldCls + (form.all_day ? " opacity-40" : "")} value={form.end_time} onChange={(e) => setF("end_time", e.target.value)} /></div>
              <div className="md:col-span-2"><label className={labelCls}>Название / причина</label><input className={fieldCls} placeholder={form.event_type === "personal_booking" ? "Например: личная экскурсия" : form.event_type === "holiday" ? "Например: отпуск" : "Необязательно"} value={form.title} onChange={(e) => setF("title", e.target.value)} /></div>
              <div className="md:col-span-2 xl:col-span-4"><label className={labelCls}>Комментарий</label><input className={fieldCls} placeholder="Необязательно" value={form.notes} onChange={(e) => setF("notes", e.target.value)} /></div>
            </div>
            <button className="btn-bronze mt-6 flex items-center gap-2" disabled={saving} onClick={save}><Save size={15} /> {saving ? "…" : (form.id ? "Сохранить изменения" : "Добавить")}</button>
          </section>
        )}

        <div className="flex flex-wrap gap-2 mb-7">
          {["all", "available", "personal_booking", "holiday", "busy", "argali_booking", "argali_tentative"].map((type) => (
            <button key={type} onClick={() => setFilter(type)} className="text-[0.62rem] uppercase tracking-[0.12em] px-3 py-2 border transition-colors"
              style={{ borderColor: filter === type ? "#A96818" : "rgba(215,184,117,0.22)", color: filter === type ? "#F4F0E7" : "#B99C67", background: filter === type ? "rgba(169,104,24,0.25)" : "transparent" }}>
              {type === "all" ? "Все" : EVENT_LABELS[type]}
            </button>
          ))}
        </div>

        {loading ? <p className="text-argali-sandstone/50">Загрузка расписания…</p> : Object.keys(grouped).length === 0 ? (
          <div className="border border-argali-brown/30 p-8 text-center"><CalendarDays size={28} className="mx-auto text-argali-sandstone/40 mb-3" /><p className="text-argali-sandstone/60">На выбранные даты записей нет.</p></div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([date, events]) => (
              <section key={date}>
                <h2 className="font-serif text-xl md:text-2xl text-argali-ivory capitalize mb-3">{formatDate(date)}</h2>
                <div className="space-y-2">
                  {events.map((event) => {
                    const startDate = datePart(event.start_at);
                    const endDate = datePart(event.end_at);
                    const when = event.all_day
                      ? (startDate === endDate ? "Весь день" : `${formatShortDate(startDate)} — ${formatShortDate(endDate)}`)
                      : (startDate === endDate ? `${timePart(event.start_at)}–${timePart(event.end_at)}` : `${formatShortDate(startDate)} ${timePart(event.start_at)} — ${formatShortDate(endDate)} ${timePart(event.end_at)}`);
                    return (
                      <article key={event.id} className="border border-argali-brown/30 px-4 md:px-5 py-4 grid grid-cols-1 md:grid-cols-[190px_1fr_auto] gap-3 md:gap-5 items-center">
                        <div>
                          <span className="text-[0.58rem] uppercase tracking-[0.14em] text-argali-gold">{EVENT_MARKS[event.event_type] || event.event_type}</span>
                          <p className="text-sm text-argali-ivory mt-1">{when}</p>
                        </div>
                        <div>
                          {manager && <p className="text-[0.6rem] uppercase tracking-wider text-argali-sandstone/45 mb-1">{event.guide_name}</p>}
                          <h3 className="text-argali-ivory">{event.title || EVENT_LABELS[event.event_type]}</h3>
                          {event.notes && <p className="text-sm text-argali-sandstone/55 mt-1">{event.notes}</p>}
                          {event.source === "argali" && <p className="text-xs text-argali-gold/70 mt-1">Из заявки АРГАЛИ · {event.request_status}</p>}
                        </div>
                        {!manager && !event.read_only && (
                          <div className="flex gap-2">
                            <button onClick={() => edit(event)} className={secondaryBtn + " flex items-center gap-1.5"}><Pencil size={13} /> Изменить</button>
                            <button onClick={() => remove(event)} className={secondaryBtn + " flex items-center gap-1.5"}><Trash2 size={13} /> Удалить</button>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
